// Shared heatmap colour helpers used across the result-view cards.
//
// Each `Palette` defines the start (low-intensity) and end (high-intensity)
// RGB stops of a slate-rooted gradient. `bgFor` lerps along the log-scaled
// intensity ramp so dense cells deepen quickly without crushing low values
// into invisibility, while `txtFor` flips the foreground to white once the
// background gets dark enough to need it.
//
// Two palettes ship by default:
//   - HEATMAP_PURPLE  — used by HoursHeatmap + PhaseConcentrationCard.
//   - HEATMAP_EMERALD — used by SummaryView (savings = good).
// New cards should extend `HeatmapPalette` rather than rolling their own
// gradient logic.

export interface HeatmapPalette {
  /** Slate base — the colour for value=0 cells. */
  base: [number, number, number];
  /** Saturated end — the colour for value=max cells. */
  peak: [number, number, number];
  /** Foreground used when the cell is dark (intensity > 0.55). */
  darkFg: string;
  /** Foreground used otherwise. */
  lightFg: string;
}

const SLATE_BASE: [number, number, number] = [248, 250, 252];

export const HEATMAP_PURPLE: HeatmapPalette = {
  base: SLATE_BASE,
  peak: [123, 91, 182],
  darkFg: 'white',
  lightFg: '#1e293b',
};

export const HEATMAP_EMERALD: HeatmapPalette = {
  base: SLATE_BASE,
  peak: [5, 150, 105],
  darkFg: 'white',
  lightFg: '#0f172a',
};

/** Logarithmic 0..1 intensity for `value` against `max` — keeps low values
 *  visible while letting peaks saturate. */
export function intensity(value: number, max: number): number {
  if (max <= 0 || value <= 0) return 0;
  const ratio = value / max;
  return Math.max(0, Math.min(1, Math.log10(1 + 9 * ratio)));
}

/** Background colour for a heatmap cell. */
export function bgFor(value: number, max: number, palette: HeatmapPalette): string {
  const i = intensity(value, max);
  const r = Math.round(palette.base[0] - i * (palette.base[0] - palette.peak[0]));
  const g = Math.round(palette.base[1] - i * (palette.base[1] - palette.peak[1]));
  const b = Math.round(palette.base[2] - i * (palette.base[2] - palette.peak[2]));
  return `rgb(${r},${g},${b})`;
}

/** Foreground colour for a heatmap cell — flips to `darkFg` once the cell
 *  is dark enough for white text to pop. */
export function txtFor(value: number, max: number, palette: HeatmapPalette): string {
  return intensity(value, max) > 0.55 ? palette.darkFg : palette.lightFg;
}
